import { PageRecordFilters } from '../components/PageRecordFilters';
import { InvoiceMonthFilter } from '../components/InvoiceMonthFilter';
import { matchesPageMonth } from '../utility/pageMonthFilter';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Group,
  Button,
  SegmentedControl,
} from '@mantine/core';
import { IconFileExcel, IconUpload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import './KanbanColumn.css';
import Environment from '../utility/environment';
import { mapApiRowToOpportunity } from '../utility/mapApiOpportunity';
import { useAuth } from '../contexts/AuthProvider';
import {Product, Opportunity, OpportunityForm, STAGES, } from '../components/OpportunityPageTypes';
import {
  OpportunityTableView,
  type OpportunityTableViewHandle,
} from '../components/OpportunityTableView';
import {  OpportunityModal } from '../components/OpportunityPageModals';
import { Options } from '../utility/types';
import type { NewContactData } from '../components/OpportunityPageModals';
import { OpportunityKanbanView } from '../components/OpportunityPageKanban';
import { OpportunityRoadMapView } from '../components/OpportunityPageRoadMap';
import { PageFilterBanner } from '../components/PageFilterBanner';
import { filterOpportunities } from '../utility/filterOpportunities';
import { readPageFilterParam } from '../utility/pageNavigationFilters';
import { TableExcelExportModal } from '../components/TableExcelExportModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import {
  applyOpportunityImportRow,
  buildOpportunityFromImportRow,
  buildOpportunityImportHeaders,
  formatOpportunityImportValidationError,
} from '../utility/opportunityExcelImport';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import {
  DELETED_COLUMN_HEADER,
  formatDeletedExportValue,
} from '../utility/excelDeletedColumn';
import {
  buildOpportunityNameMaps,
  formatOpportunityListRow,
} from '../components/OpportunityPageUtilities';
import {
  datedExportFilename,
  EXISTING_RECORD_ID_HEADER,
  exportRowsToExcel,
  type TableExportOptions,
} from '../utility/tableExcelExport';
import { readApiErrorMessage } from '../utility/apiErrors';
import { getStageLabel } from '../i18n/stageLabels';

/* ───────────────────────────────
   COMPONENTE PRINCIPAL: OpportunityPage
──────────────────────────────── */

export default function OpportunityPage() {
  const { t } = useTranslation();
  const { accessToken, actualEnterprise, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const opportunityIdFilter = readPageFilterParam(location.search, 'opportunityId');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [viewMode, setViewMode] = useState<'table' | 'kanban' | 'roadmap'>('table');
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  // const [opportunitiesOptions, setOportunitiesOptions] = useState<Options[]>([]);
  const [clientsOptions, setClientsOptions] = useState<Options[]>([]);
  const [contactsOptions, setContactOptions] = useState<Options[]>([]);
  const [productsOptions, setProductsOptions] = useState<Options[]>([]);
  const [campaignsOptions, setCampaignsOptions] = useState<Options[]>([]);
  const [ownerOptions, setOwnerOptions] = useState<Options[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [minFilter, setMinFilter] = useState<number | ''>('');
  const [maxFilter, setMaxFilter] = useState<number | ''>('');
  const [contactFilter, setContactFilter] = useState<string | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [__, setRemovedFiles] = useState<string[]>([]);
  const tableRef = useRef<OpportunityTableViewHandle>(null);
  const activeEnterpriseIdRef = useRef<string | null>(null);
  const [fallbackExportOpen, setFallbackExportOpen] = useState(false);
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importNotification, setImportNotification] = useState<{
    message: string;
    color: 'green' | 'red';
  } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);

  const openOpportunityModal = (opp?: Opportunity) => {
    getClientContacts();
    setEditingOpportunity(opp || null);
    setRemovedFiles([]);

    setModalOpened(true);
  };

  const getClients = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/clients_options/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('Clients Data:', data);
        setClientsOptions(data.map((client: any) => {return {value: client.id, label: client.legal_name}}).sort((a:any, b: any) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  }

  const getClientContacts = async () => {
    try {
      const res = await fetch(`${Environment.VITE_API_URL}/api/v1/contacts_options?enterprise_id=${actualEnterprise?.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        credentials: "include",
      });
  
      const data = await res.json();
  
      const contacts: Options[] = data.map((contact: any) => ({
        value: contact.id,
        label: contact.name,
      }));
  
      setContactOptions(contacts.sort((a:any, b: any) => a.label.localeCompare(b.label)));

    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getProducts = () => {
      fetch(`${Environment.VITE_API_URL}/api/v1/soldable_products/${actualEnterprise?.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        credentials: "include",
      })
      .then((res) => res.json())
      .then((data) => {
        const formattedProducts: Product[] = data.map((opportunity: any) => ({
          productId: opportunity.id,
          productName: opportunity.name,
          quantity: opportunity.quantity,
          currency: opportunity.currency,
          price: opportunity.price,
          discount: parseFloat(opportunity.discount),
        }));
        console.log('Formatted Products:', formattedProducts);
        const sortedProducts = formattedProducts.sort((a, b) => a.productName.localeCompare(b.productName));
        setProducts(sortedProducts);
        const products: Options[] =sortedProducts.map((product) => ({
          value: product.productId,
          label: product.productName
        }));
        setProductsOptions(products.sort((a, b) => a.label.localeCompare(b.label)));
    })
    .catch((error) => console.error(error));
  };

  const getOwners = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/enterprise_users/${actualEnterprise?.id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const owners: Options[] = data.map((u: { id: string; name: string }) => ({
          value: u.id,
          label: u.name,
        }));
        setOwnerOptions(owners.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch((error) => console.error(error));
  };

  const getCampaigns = () => {
      fetch(`${Environment.VITE_API_URL}/api/v1/all_campaigns/${actualEnterprise?.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        credentials: "include",
      })
      .then((res) => res.json())
      .then((data) => {
        const campaigns: Options[] = data.map((campaign: any) => ({
          value: campaign.id,
          label: campaign.name
        }));
        console.log('Formatted Campaigns:', campaigns);
        setCampaignsOptions(campaigns.sort((a, b) => a.label.localeCompare(b.label)));
    })
    .catch((error) => console.error(error));
  };

  useEffect(() => {
    const enterpriseId = actualEnterprise?.id ?? null;
    activeEnterpriseIdRef.current = enterpriseId;
    if (!accessToken || !enterpriseId) {
      setOpportunities([]);
      return;
    }

    setOpportunities([]);

    getOpportunities(enterpriseId);
    getClients();
    getClientContacts();
    getProducts();
    getCampaigns();
    getOwners();
  }, [accessToken, actualEnterprise?.id]);

  const parseToPTBRStageName = (stageName: string) => {
    switch (stageName) {
      case 'Value Proposition':
        return 'Proposta de Valor';
      case 'Quotation':
        return 'Quotação';
      case 'Demonstration':
        return 'Demonstração';
      case 'Negociation':
        return 'Negociação';
      case 'Closed':
        return 'Fechamento';
      default:
        return stageName;
    }
  }

  const formatOpportunityForCreate = (opp: OpportunityForm) => {
    let stage = opp.stages.map((stage) => ({
      name: parseToPTBRStageName(stage.name),
      start_date: stage.created_at,
      finished_date: stage?.finished_at || null,
    })).sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())[0]
    stage.start_date = opp.startDate || new Date().toISOString();
    return {
      name: opp.name,
      client_ids: opp.client_ids,
      contact_id: opp.contact_id || null,
      user_id: opp.user_id,
      owner: opp.owner || null,
      campaign_id: opp.campaign_id==="" ? null : opp.campaign_id,
      value: opp.value,
      probability: opp.probability,
      discount: opp.discount,
      decision_maker_id: opp.decision_maker,
      influencer_id: opp.influencer,
      investidor_id: opp.investidor,
      client_image: opp.client_image,
      no_sales_made: opp.noSalesMade || false,
      remito: opp.remito,
      products: opp.products.map((product) => {
        return {
          opportunity_id: "",
          product_id: product.productId,
          sales_package_id: product.salesPackageId || null,
          sales_package_name: product.salesPackageName || null,
          discount_type: product.discountType || null,
          discount_value: product.discountValue ?? null,
          name: product.productName,
          quantity: product.quantity,
          currency: product.currency,
          deal_price: product.price,
          discount: product.discount,
          sold: product.sold || false,
          payment: {
            tipo: product.payment?.tipo || 'avista',
            data_avista: product.payment?.data?.toISOString().split('T')[0] || null,
            client_id: product.payment?.client_id || null,
            parcelas: product.payment?.parcelas?.map((parcela, index) => ({
              parcela_num: index + 1,
              data: parcela?.data?.toISOString().split('T')[0],
              client_id: parcela?.client_id || null,
              amount: parcela?.amount ?? null,
            }))
          }
        }
      }),
      files: opp.files.map((file) => {
        return {
          file_url: file.file,
          file_name: file.file_name,
          size: file.size,
        }
      }),
      stage: stage,
    }
  }

  const formatOpportunityForUpdate = (updatedOpp: OpportunityForm, editingOpportunity: OpportunityForm | null) => {

    let stages = updatedOpp.stages.map((stage) => ({
        name: parseToPTBRStageName(stage.name),
        start_date: stage.created_at,
        finished_date: stage?.finished_at || null,
      }));
    
    let stagesFiltered, selectedStage;

    // New stage is being added
    if (updatedOpp.stages.length !== editingOpportunity?.stages.length) {
      stagesFiltered = stages.filter((stage) => stage.finished_date === null);
      selectedStage = stagesFiltered[stagesFiltered.length - 1] || stagesFiltered[0];
      selectedStage.start_date = new Date().toISOString();
    } else {
      const firstStageFound = stages.find((stage) => stage.name === 'Fechamento');
      if (firstStageFound) {
        selectedStage = firstStageFound;
      } else {
        const stagesSorted = [...stages].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
        selectedStage = stagesSorted[0];
      }
      selectedStage.start_date = updatedOpp.startDate;
    }

    return {
      name: updatedOpp.name,
      client_ids: updatedOpp.client_ids,
      contact_id: updatedOpp.contact_id || null,
      user_id: updatedOpp.user_id,
      owner: updatedOpp.owner || null,
      campaign_id: updatedOpp.campaign_id,
      value: updatedOpp.value,
      probability: updatedOpp.probability,
      discount: updatedOpp.discount,
      decision_maker_id: updatedOpp.decision_maker,
      influencer_id: updatedOpp.influencer,
      investidor_id: updatedOpp.investidor,
      client_image: updatedOpp.client_image,
      no_sales_made: updatedOpp.noSalesMade || false,
      remito: updatedOpp.remito,
      products: updatedOpp.products.map((product) => {
        return {
          opportunity_id: "",
          product_id: product.productId,
          sales_package_id: product.salesPackageId || null,
          sales_package_name: product.salesPackageName || null,
          discount_type: product.discountType || null,
          discount_value: product.discountValue ?? null,
          name: product.productName,
          quantity: product.quantity,
          currency: product.currency,
          deal_price: product.price,
          discount: product.discount,
          sold: product.sold || false,
          payment: {
            tipo: product.payment?.tipo || 'avista',
            data_avista: product.payment?.data?.toISOString().split('T')[0] || null,
            client_id: product.payment?.client_id || null,
            parcelas: product.payment?.parcelas?.map((parcela, index) => ({
              parcela_num: index + 1,
              data: parcela?.data?.toISOString().split('T')[0],
              client_id: parcela?.client_id || null,
              amount: parcela?.amount ?? null,
            }))
          }
        }
      }),
      files: updatedOpp.files.map((file) => {
        return {
          file_url: file.file,
          file_name: file.file_name,
          size: file.size,
        }
      }),
      stage: selectedStage,
    }
  }

  const getOpportunities = (enterpriseId: string) => {
    fetch(`${Environment.VITE_API_URL}/api/v1/opportunities/${enterpriseId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
    .then(async (res) => {
      if (!res.ok) {
        console.error('Failed to load opportunities', res.status);
        return null;
      }
      return res.json();
    })
    .then((data) => {
        if (!Array.isArray(data) || activeEnterpriseIdRef.current !== enterpriseId) {
          return;
        }
        const formattedOpportunities: Opportunity[] = data.map((row: any) =>
          mapApiRowToOpportunity(row),
        );
        setOpportunities(formattedOpportunities);
    })
  }
  
  // const removeFiles = () => {
  //   // removedImages.forEach((image) => {
  //     // if (image.startsWith('/')) {
  //     //   image = image.substring(1);
  //     // }
  //     // console.log(JSON.stringify({image_url: image}));
  //     console.log('Removed FILES:', removedFiles);
  //     fetch(`${Environment.VITE_API_URL}/api/v1/delete-file`, {
  //       method: 'DELETE',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${accessToken}`,
  //       },
  //       credentials: 'include',
  //       body: JSON.stringify(removedFiles), //JSON.stringify(image),
  //     })
  //       .then((res) => res.json())
  //       .then((data) => {
  //         console.log(data);
  //       })
  //       .catch((error) => {
  //         console.error(error);
  //       })
        
  //   // });

  // }

    const sendFiles = async(oppForm: OpportunityForm) => {
    console.log('Sending files:', oppForm?.files);
    const files = oppForm?.files;
    for (let index = 0; index < (files?.length ?? 0); index++) {
      const file = files && index >= 0 && index < files.length ? files[index] : null;
      if (file?.file instanceof File) {
        const formData = new FormData();
          formData.append('file', file?.file);
          try {
            const response = await fetch(`${Environment.VITE_API_URL}/api/v1/upload-file`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
              credentials: 'include',
              body: formData,
            })

            if (response.ok) {
              const data = await response.json();
              file.file = data.file_url;
              setEditingOpportunity((prev) => {
                if (prev) {
                  const prevFiles = [...prev.files];
                  if (prevFiles[index]) {
                    prevFiles[index].file = data.file_url;
                  }
                  return {...prev, files: prevFiles};
                }
                return prev;
              });
            }
        } catch (error) {
        }
      }
    }    
  }

  const saveOpportunity = async (oppForm: OpportunityForm) => {
    if (!actualEnterprise?.id) {
      alert(t('opportunity.saveFailed'));
      return;
    }

    await sendFiles(oppForm);

    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/opportunities?enterprise_id=${encodeURIComponent(actualEnterprise.id)}`,
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatOpportunityForCreate(oppForm)),
      });

      if (!response.ok) {
        const message = await readApiErrorMessage(response, t('opportunity.saveFailed'));
        alert(message);
        return;
      }

      setEditingOpportunity(null);
      setModalOpened(false);
    } catch (error) {
      console.error(error);
      alert(t('opportunity.saveFailed'));
    } finally {
      if (actualEnterprise?.id) {
        getOpportunities(actualEnterprise.id);
      }
    }
  };

  const deleteOpportunity = () => {
    if (!editingOpportunity?.id) {
      return;
    }

    fetch(`${Environment.VITE_API_URL}/api/v1/opportunity/${editingOpportunity.id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail = typeof data.detail === 'string' ? data.detail : t('opportunity.deleteFailed');
          throw new Error(detail);
        }
        return data;
      })
      .then(() => {
        setEditingOpportunity(null);
        setModalOpened(false);
      })
      .catch((error) => {
        console.error(error);
        alert(error instanceof Error ? error.message : t('opportunity.deleteFailed'));
      })
      .finally(() => {
        if (actualEnterprise?.id) {
          getOpportunities(actualEnterprise.id);
        }
      });
  };

  const createContactAdHoc = async (data: NewContactData): Promise<Options> => {
    const clients = data.client_ids?.length
      ? data.client_ids.map((clientId) => ({
          legal_name: clientsOptions.find((c) => c.value === clientId)?.label || '',
          email: data.email,
          phone_number: data.phone_number,
        }))
      : [];
    const body = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone_number: data.phone_number,
      position: data.position || null,
      contact_date: new Date().toISOString(),
      notes: '',
      campaign_id: null,
      clients,
    };
    const res = await fetch(`${Environment.VITE_API_URL}/api/v1/new_contacts/${actualEnterprise?.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`Failed to create contact (${res.status})`);
    }
    const created = await res.json();
    const option: Options = {
      value: created.id,
      label: `${created.first_name} ${created.last_name}`.trim(),
    };
    setContactOptions((prev) => {
      if (prev.some((o) => o.value === option.value)) return prev;
      return [...prev, option].sort((a, b) => a.label.localeCompare(b.label));
    });
    return option;
  };

  const updateOpportunity = async(updatedOpp: OpportunityForm, id: string)  =>{
    console.log('Updating opportunity:', updatedOpp);
    console.log("Editing opportunity:", editingOpportunity);

    const stage = editingOpportunity?.stages.find((stage) => stage.name === updatedOpp.stages.filter((stage) => stage.finished_at === null || stage.finished_at === undefined)[0].name)
    if (stage?.finished_at !== null && stage?.finished_at !== undefined) {
      alert(t('opportunity.stageAlreadyExists'));
      return;
    }
    // console.log("Stage:", updatedOpp.stages.filter((stage) => stage.finished_at === null || stage.finished_at === undefined)[0]);
    // if (editingOpportunity?.stages.filter((stage) => stage.finished_at === null || stage.finished_at === undefined)[0].name  === "Closed"){
    //   alert("Oportunidade já está na fase de fechamento.")
    //   return;
    // }

    
    await sendFiles(updatedOpp);

    const updatedOppFormated = formatOpportunityForUpdate(updatedOpp, editingOpportunity);

    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/opportunities/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(updatedOppFormated),
      });

      if (!response.ok) {
        const message = await readApiErrorMessage(response, t('opportunity.updateFailed'));
        alert(message);
        return;
      }

      setEditingOpportunity(null);
      setModalOpened(false);
    } catch (error) {
      console.error(error);
      alert(t('opportunity.updateFailed'));
    } finally {
      if (actualEnterprise?.id) {
        getOpportunities(actualEnterprise.id);
      }
    }
  }



  useEffect(() => {
    document.title = t('opportunity.documentTitle');
  }, [t]);

  const filterValueMax = useMemo(()=>Math.max(1,Math.ceil(opportunities.reduce((max,opp)=>Math.max(max,Number(opp.value)||0),0))),[opportunities]);
  const filterProductOptions = useMemo(()=>{
    const items=new Map<string,string>();
    opportunities.forEach(opp=>opp.products.forEach(product=>items.set(product.productId,product.productName||product.productId)));
    return Array.from(items,([value,label])=>({value,label})).sort((a,b)=>a.label.localeCompare(b.label));
  },[opportunities]);
  const filteredOpportunities = useMemo(
    () =>
      filterOpportunities(opportunities, {
        clientIds: clientFilter,
        stageNames: stageFilter, productIds: productFilter,
        minValue: user?.role === 'admin' ? minFilter : undefined, maxValue: user?.role === 'admin' ? maxFilter : undefined,
        contactId: contactFilter,
        ownerId: ownerFilter,
        opportunityId: opportunityIdFilter,
      }).filter(opportunity => matchesPageMonth(opportunity.startDate, selectedMonth)),
    [opportunities, clientFilter, contactFilter, ownerFilter, opportunityIdFilter, selectedMonth, stageFilter, productFilter, minFilter, maxFilter, user?.role],
  );

  const filteredOpportunityName = useMemo(() => {
    if (!opportunityIdFilter) return '';
    return filteredOpportunities[0]?.name ?? opportunityIdFilter;
  }, [filteredOpportunities, opportunityIdFilter]);

  const importHeaders = useMemo(() => buildOpportunityImportHeaders(t), [t]);

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setImportNotification(null);
  };

  const openFallbackExport = () => {
    if (filteredOpportunities.length === 0) {
      alert(t('opportunity.noExport'));
      return;
    }
    setFallbackExportOpen(true);
  };

  const executeOpportunityImport = async (
    createRows: Opportunity[],
    updateEntries: Array<{ id: string; opportunity: Opportunity; existing: Opportunity }>,
    deleteIds: string[],
  ) => {
    const ok = await executeImportUpsert({
      createPayloads: createRows.map((row) => formatOpportunityForCreate(row)),
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: formatOpportunityForUpdate(entry.opportunity, entry.existing),
      })),
      deleteIds,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/opportunities/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/opportunity/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setImportNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setImportNotification({ message: t('common.import.success'), color: 'green' });
    setImportModalOpened(false);
    if (actualEnterprise?.id) {
      getOpportunities(actualEnterprise.id);
    }
  };

  const handleAutoImport = async () => {
    if (!autoImportFile || !user?.id) {
      return;
    }
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(opportunities.map((opportunity) => opportunity.id));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const createRows: Opportunity[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const built = buildOpportunityFromImportRow(
          row,
          importHeaders,
          ownerOptions,
          contactsOptions,
          clientsOptions,
          user.id,
        );
        if (built.errors.length > 0) {
          errors.push(formatOpportunityImportValidationError(t, line, built.errors));
        } else if (built.opportunity) {
          createRows.push(built.opportunity);
        }
      }

      const updateEntries: Array<{ id: string; opportunity: Opportunity; existing: Opportunity }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = opportunities.find((opportunity) => opportunity.id === entry.id);
        if (!existing) {
          continue;
        }
        const merged = applyOpportunityImportRow(
          existing,
          entry.row,
          importHeaders,
          ownerOptions,
          contactsOptions,
          clientsOptions,
        );
        if (!merged.name.trim()) {
          errors.push(formatOpportunityImportValidationError(t, line, ['name']));
          continue;
        }
        updateEntries.push({ id: entry.id, opportunity: merged, existing });
      }

      if (errors.length > 0) {
        setImportNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createRows.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeOpportunityImport(createRows, updateEntries, classified.deleteIds),
      });
    } catch {
      setImportNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={importNotification}
    />
  );

  const handleFallbackExport = (exportOptions: TableExportOptions) => {
    const { ownerNameById, contactNameById } = buildOpportunityNameMaps(ownerOptions, contactsOptions);
    const headers = [
      ...(exportOptions.includeIds ? [EXISTING_RECORD_ID_HEADER] : []),
      t('opportunity.table.owner'),
      t('opportunity.table.client'),
      t('opportunity.table.contact'),
      t('opportunity.table.opportunity'),
      t('opportunity.table.remito'),
      t('opportunity.table.value'),
      t('opportunity.table.discount'),
      t('opportunity.table.stage'),
      t('opportunity.table.totalTimeDays'),
    ];
    if (exportOptions.includeDeletedColumn) {
      headers.push(DELETED_COLUMN_HEADER);
    }
    const rows = filteredOpportunities.map((opportunity) => {
      const row = formatOpportunityListRow(opportunity, ownerNameById, contactNameById);
      const values = [
        row.owner_name,
        row.client,
        row.contact_name,
        row.name,
        row.remito,
        Number.parseFloat(String(row.value ?? 0)) || 0,
        row.discount,
        getStageLabel(row.current_stage, t),
        row.total_duration,
      ];
      const baseRow = exportOptions.includeIds ? [opportunity.id, ...values] : values;
      if (exportOptions.includeDeletedColumn) {
        return [...baseRow, formatDeletedExportValue(Boolean(opportunity.deletedAt))];
      }
      return baseRow;
    });
    exportRowsToExcel(headers, rows, datedExportFilename('opportunities'), 'Opportunities');
    setFallbackExportOpen(false);
  };

  return (
    <Container fluid style={{ padding: 0 }}>
      <Group position="apart" align="center" mb="md">
        <Group spacing="lg">
          <Title order={1}>{t('opportunity.title')}</Title>
          <InvoiceMonthFilter value={selectedMonth} onChange={setSelectedMonth} label={t('common.monthFilter.startDate')}/>
        </Group>
        <Group>
          <Button variant="subtle"
            onClick={()=>{setClientFilter([]);setContactFilter(null);setOwnerFilter(null);setStageFilter([]);setProductFilter([]);setMinFilter('');setMaxFilter('');setSelectedMonth('all');tableRef.current?.clearFilters();}}>{t('invoices.filters.clear')}</Button>
          <SegmentedControl
            value={viewMode}
            onChange={(value) => setViewMode(value as 'table' | 'kanban' | 'roadmap')}
            data={[
              { label: t('common.view.table'), value: 'table' },
              { label: t('common.view.kanban'), value: 'kanban' },
              { label: t('common.view.roadmap'), value: 'roadmap' }
            ]}
          />
          {viewMode !== 'table' ? (
            <>
              <Button
                variant="light"
                color="brand"
                size="xs"
                leftIcon={<IconUpload size={16} />}
                onClick={openImportModal}
              >
                {t('common.import.excel')}
              </Button>
              <Button
                variant="light"
                color="green"
                size="xs"
                leftIcon={<IconFileExcel size={16} />}
                onClick={openFallbackExport}
              >
                {t('common.export.excel')}
              </Button>
            </>
          ) : null}
          <Button onClick={() => openOpportunityModal()}>{t('opportunity.addOpportunity')}</Button>
        </Group>
      </Group>

      <PageRecordFilters fields={[
        {key:'owner',label:t('opportunity.filters.owner'),placeholder:t('opportunity.filters.allOwners'),options:ownerOptions,value:ownerFilter?[ownerFilter]:[],onChange:value=>setOwnerFilter(value[0]||null),single:true},
        {key:'stage',label:t('opportunity.table.stage'),placeholder:t('common.recordFilters.allStages'),options:STAGES.map(value=>({value,label:getStageLabel(value,t)})),value:stageFilter,onChange:setStageFilter},
        {key:'client',label:t('opportunity.filters.client'),placeholder:t('invoices.filters.allClients'),options:clientsOptions,value:clientFilter,onChange:setClientFilter},
        {key:'product',label:t('invoices.filters.products'),placeholder:t('invoices.filters.allProducts'),options:filterProductOptions,value:productFilter,onChange:setProductFilter},
        {key:'contact',label:t('opportunity.filters.contact'),placeholder:t('opportunity.filters.allContacts'),options:contactsOptions,value:contactFilter?[contactFilter]:[],onChange:value=>setContactFilter(value[0]||null),single:true},
      ]} range={user?.role==='admin'?{min:0,max:filterValueMax,value:[minFilter===''?0:minFilter,maxFilter===''?filterValueMax:maxFilter],onChange:([min,max])=>{setMinFilter(min===0?'':min);setMaxFilter(max===filterValueMax?'':max);},label:t('invoices.filters.valueRange'),currency:'USD'}:undefined}/>


      {opportunityIdFilter && (
        <PageFilterBanner
          message={t('opportunity.filteredByOpportunity', { name: filteredOpportunityName })}
          onClear={() => navigate('/opportunity')}
        />
      )}

      {viewMode === 'table' ? (
        <OpportunityTableView
          ref={tableRef}
          opportunities={filteredOpportunities}
          ownerOptions={ownerOptions}
          contactOptions={contactsOptions}
          onClick={openOpportunityModal}
          onOpenImport={openImportModal}
        />
      ) : viewMode === 'kanban' ? (
        <OpportunityKanbanView opportunities={filteredOpportunities} updateOpportunity={updateOpportunity} onClick={openOpportunityModal} />
      ) : (
        <OpportunityRoadMapView opportunities={filteredOpportunities} updateOpportunity={updateOpportunity} onClick={openOpportunityModal} />
      )}
      <OpportunityModal
        opened={modalOpened}
        onClose={() => {setEditingOpportunity(null); setModalOpened(false); console.log('Modal closed')}} // Limpa o estado de edição ao fechar o modal
        onSave={saveOpportunity}
        onUpdate={updateOpportunity}
        onDelete={deleteOpportunity}
        removeFiles={(file) => {
          setRemovedFiles((prev) => [...prev, file]);
        }}
        productOptions={productsOptions}
        products={products}
        clientOptions={clientsOptions}
        contactOptions={contactsOptions}
        campaignOptions={campaignsOptions}
        ownerOptions={ownerOptions}
        editingOpportunity={editingOpportunity}
        onCreateContact={createContactAdHoc}
      />
      {importModal}
      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="opportunities"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />
      <TableExcelExportModal
        opened={fallbackExportOpen}
        onClose={() => setFallbackExportOpen(false)}
        onExport={handleFallbackExport}
      />
    </Container>
  );
}
